/**
 * 429 쿼터 초과 응답을 처리하는 훅
 * UpgradeModal 트리거 상태 관리
 */
import { useState, useCallback } from 'react'
import type { AxiosError } from 'axios'

interface QuotaDetail {
  error: 'QUOTA_EXCEEDED'
  feature: string
  limit: number
  used: number
  reset_at?: string
  upgrade_url?: string
}

interface QuotaState {
  open: boolean
  feature?: string
  info?: { limit: number; used: number; reset_at?: string; upgrade_url?: string }
}

export function useQuotaGuard() {
  const [quota, setQuota] = useState<QuotaState>({ open: false })

  // axios 에러 또는 SSE demo_limit 이벤트에서 호출
  const handleQuotaError = useCallback((errOrData: AxiosError | string | unknown) => {
    if (typeof errOrData === 'string') {
      // SSE demo_limit 이벤트 — JSON 파싱 시도
      try {
        const parsed = JSON.parse(errOrData) as Partial<QuotaDetail>
        setQuota({
          open: true,
          feature: parsed.feature,
          info: parsed.limit != null
            ? { limit: parsed.limit, used: parsed.used ?? 0, reset_at: parsed.reset_at, upgrade_url: parsed.upgrade_url }
            : undefined,
        })
      } catch {
        setQuota({ open: true })
      }
      return true
    }

    const axiosErr = errOrData as AxiosError
    if (axiosErr?.response?.status === 429) {
      const detail = (axiosErr.response.data as { detail?: QuotaDetail })?.detail
      setQuota({
        open: true,
        feature: detail?.feature,
        info: detail?.limit != null
          ? { limit: detail.limit, used: detail.used, reset_at: detail.reset_at, upgrade_url: detail.upgrade_url }
          : undefined,
      })
      return true
    }
    return false
  }, [])

  const closeQuota = useCallback(() => setQuota({ open: false }), [])

  return { quota, handleQuotaError, closeQuota }
}
